import { z } from '@uturn/portal/zod';
import { getRequiredFields, RequiredField } from '@uturn/portal/zod/utils';

/** @see AddressDto — top-level organisation address (Chamber of commerce). */
const organisationAddressSchema = z
	.object({
		countryCode: z.string().min(2).max(2).describe(RequiredField),
	})
	.passthrough();

/**
 * @see AddressDto — invoice address. Optional as a whole, but if any field
 * is filled, `postalCode` and `countryCode` become required (matches the
 * backend contract where `postalCode` and `countryCode` are required on
 * `AddressDto`).
 */
const invoiceAddressSchema = z
	.object({
		street: z.string().optional(),
		houseNumber: z.string().optional(),
		postalCode: z.string().optional(),
		city: z.string().optional(),
		countryCode: z.string().optional(),
		latitude: z.number().optional(),
		longitude: z.number().optional(),
	})
	.superRefine((address, ctx) => {
		const hasAnyField = [
			address.street,
			address.houseNumber,
			address.postalCode,
			address.city,
			address.countryCode,
		].some((value) => value !== undefined && value !== '');

		if (!hasAnyField) {
			return;
		}

		if (!address.postalCode) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'required',
				path: ['postalCode'],
			});
		}
		if (!address.countryCode || address.countryCode.length < 2) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'required',
				path: ['countryCode'],
			});
		}
	});

/** @see FinancialDetailsDto */
const financialDetailsSchema = z
	.object({
		address: invoiceAddressSchema.optional(),
	})
	.passthrough();

/** @see OrganizationDto (billing form subset) */
export const billingFormSchema = z
	.object({
		address: organisationAddressSchema,
		financialDetails: financialDetailsSchema,
	})
	.passthrough();

export const billingRequiredFields = getRequiredFields(billingFormSchema);
